import { useState, useMemo } from 'react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { CalendarIcon, ChevronDown, Search } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Calendar } from '@/shared/components/ui/calendar'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { cn } from '@/shared/lib/utils'
import { getDefaultClassNames, type DayButton } from 'react-day-picker'
import type { DateRange } from 'react-day-picker'

function TealDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <Button
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:bg-teal-500 data-[selected-single=true]:text-white data-[range-middle=true]:bg-teal-50 data-[range-middle=true]:text-teal-800 data-[range-start=true]:bg-teal-500 data-[range-start=true]:text-white data-[range-end=true]:bg-teal-500 data-[range-end=true]:text-white group-data-[focused=true]/day:border-teal-400 group-data-[focused=true]/day:ring-teal-400/50 dark:hover:text-accent-foreground flex aspect-square size-[var(--cell-size)] flex-col gap-0.5 leading-none font-bold text-gray-600 group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export interface DashboardFilterValues {
  dateRange?: DateRange
  farmacias?: string[]
  provincias?: string[]
  promotores?: string[]
  nombre_farmacias?: string[]
  direcciones?: string[]
  registroTonal?: 'validado' | 'sin_validar'
}

interface DashboardFiltersProps {
  filters: DashboardFilterValues
  onFiltersChange: (filters: DashboardFilterValues) => void
  opciones?: {
    farmacias: string[]
    provincias: string[]
    promotores: string[]
    nombre_farmacias?: string[]
    direcciones?: string[]
  }
  isLoading?: boolean
  showEstado?: boolean
}

function MultiSelectFilter({
  label,
  options,
  selected,
  onToggle,
  onClear,
}: {
  label: string
  options: string[]
  selected: string[]
  onToggle: (value: string) => void
  onClear: () => void
}) {
  const [search, setSearch] = useState('')

  const filtered = useMemo(() => {
    if (!search.trim()) return options
    const term = search.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(term))
  }, [options, search])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-full justify-between text-xs"
        >
          {selected.length > 0
            ? `${selected.length} ${label.toLowerCase()}`
            : label}
          <ChevronDown className="ml-1.5 h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-2" align="start">
        {options.length > 5 && (
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder={`Buscar ${label.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs pl-7"
            />
          </div>
        )}
        <div className="max-h-[200px] overflow-y-auto space-y-1">
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">Sin resultados</p>
          )}
          {filtered.map((option) => (
            <label
              key={option}
              className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(option)}
                onCheckedChange={() => onToggle(option)}
                className="h-3.5 w-3.5 border-teal-600 data-[state=checked]:bg-teal-600 data-[state=checked]:text-white"
              />
              <span className="text-xs truncate">{option}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-6 text-xs mt-1"
            onClick={onClear}
          >
            Limpiar
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
}

export function DashboardFilters({
  filters,
  onFiltersChange,
  opciones,
  isLoading,
  showEstado = true,
}: DashboardFiltersProps) {
  const handleDateChange = (range: DateRange | undefined) => {
    onFiltersChange({ ...filters, dateRange: range })
  }

  const handleClearDate = () => {
    onFiltersChange({ ...filters, dateRange: undefined })
  }

  const handleToggle = (key: 'farmacias' | 'provincias' | 'promotores' | 'nombre_farmacias' | 'direcciones', value: string) => {
    const current = filters[key] || []
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value]
    onFiltersChange({ ...filters, [key]: next.length > 0 ? next : undefined })
  }

  const hasNombreFarmacias = (opciones?.nombre_farmacias?.length ?? 0) > 0
  const hasDirecciones = (opciones?.direcciones?.length ?? 0) > 0
  const extraCols = (hasNombreFarmacias ? 1 : 0) + (hasDirecciones ? 1 : 0)
  const baseCols = 4 + extraCols
  const totalCols = showEstado ? baseCols + 1 : baseCols

  if (isLoading) return null

  return (
    <div className={`grid gap-2 mt-2`} style={{ gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))` }}>
      {/* Date Range Picker */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-full justify-start text-xs"
          >
            <CalendarIcon className="mr-1.5 h-3 w-3" />
            {filters.dateRange?.from ? (
              filters.dateRange.to ? (
                <>
                  {format(filters.dateRange.from, 'dd MMM', { locale: es })} -{' '}
                  {format(filters.dateRange.to, 'dd MMM yyyy', { locale: es })}
                </>
              ) : (
                format(filters.dateRange.from, 'dd MMM yyyy', { locale: es })
              )
            ) : (
              'Fecha'
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={filters.dateRange}
            onSelect={handleDateChange}
            numberOfMonths={2}
            locale={es}
            weekStartsOn={0}
            formatters={{
              formatWeekdayName: (date) => ['D', 'L', 'M', 'X', 'J', 'V', 'S'][date.getDay()],
            }}
            hideNavigation={false}
            hidden={{ dayOfWeek: [] }}
            showOutsideDays={false}
            classNames={{
              today: "bg-teal-50 text-teal-800 rounded-md data-[selected=true]:rounded-none",
              range_start: "rounded-l-md bg-teal-100",
              range_end: "rounded-r-md bg-teal-100",
            }}
            components={{
              DayButton: TealDayButton,
            }}
          />
          {filters.dateRange?.from && (
            <div className="border-t px-3 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-6 text-xs"
                onClick={handleClearDate}
              >
                Limpiar
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {/* Multi-select filters */}
      <MultiSelectFilter
        label="Farmacia"
        options={opciones?.farmacias || []}
        selected={filters.farmacias || []}
        onToggle={(v) => handleToggle('farmacias', v)}
        onClear={() => onFiltersChange({ ...filters, farmacias: undefined })}
      />
      <MultiSelectFilter
        label="Provincia"
        options={opciones?.provincias || []}
        selected={filters.provincias || []}
        onToggle={(v) => handleToggle('provincias', v)}
        onClear={() => onFiltersChange({ ...filters, provincias: undefined })}
      />
      <MultiSelectFilter
        label="Promotor"
        options={opciones?.promotores || []}
        selected={filters.promotores || []}
        onToggle={(v) => handleToggle('promotores', v)}
        onClear={() => onFiltersChange({ ...filters, promotores: undefined })}
      />
      {hasNombreFarmacias && (
        <MultiSelectFilter
          label="Nombre Farmacia"
          options={opciones?.nombre_farmacias || []}
          selected={filters.nombre_farmacias || []}
          onToggle={(v) => handleToggle('nombre_farmacias', v)}
          onClear={() => onFiltersChange({ ...filters, nombre_farmacias: undefined })}
        />
      )}
      {hasDirecciones && (
        <MultiSelectFilter
          label="Dirección"
          options={opciones?.direcciones || []}
          selected={filters.direcciones || []}
          onToggle={(v) => handleToggle('direcciones', v)}
          onClear={() => onFiltersChange({ ...filters, direcciones: undefined })}
        />
      )}

      {/* Filtro estado - solo visible en tabs con estado (registros) */}
      {showEstado && (
        <Select
          value={filters.registroTonal || 'todos'}
          onValueChange={(v) =>
            onFiltersChange({
              ...filters,
              registroTonal: v === 'todos' ? undefined : (v as 'validado' | 'sin_validar'),
            })
          }
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Registro Tonal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="validado">Validados</SelectItem>
            <SelectItem value="sin_validar">Pendiente</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
